import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SchemaOrg from '@/components/feature/SchemaOrg';
import SEOMeta from '@/components/feature/SEOMeta';

const testimonials = [
  {
    id: 't1',
    name: '王建国',
    title: '鲜果园品牌总监',
    content: '黄瓜传媒的团队非常专业，从品牌诊断到策略落地，每一步都扎实有力。品牌升级后的市场反馈远超预期。',
  },
  {
    id: 't2',
    name: '李婷',
    title: '云智科技市场VP',
    content: '企业宣传片的制作水准令人惊叹，从脚本创意到后期成片，整个流程高效且品质极高，强烈推荐给需要专业视频服务的企业。',
  },
  {
    id: 't3',
    name: '张明',
    title: '锦绣家居创始人',
    content: '小红书运营项目让我们在3个月内实现了品牌声量的爆发式增长，ROI远超行业平均水平，黄瓜传媒的运营方法论非常值得信任。',
  },
  {
    id: 't4',
    name: '陈雪',
    title: '悦享美妆电商总监',
    content: '从抖音直播运营到达人矩阵搭建，黄瓜传媒展现了极强的执行力。月均GMV从200万提升到1200万，合作效果有目共睹。',
  },
];

const values = [
  {
    icon: 'ri-lightbulb-line',
    title: '创意驱动',
    desc: '我们相信创意是品牌传播的灵魂，每一个项目都从独特的洞察出发。',
  },
  {
    icon: 'ri-bar-chart-line',
    title: '数据导向',
    desc: '用数据验证假设，用结果衡量价值，让每一次传播都有据可依。',
  },
  {
    icon: 'ri-shake-hands-line',
    title: '客户至上',
    desc: '深度理解客户需求，以合作伙伴的姿态共同实现品牌目标。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续迭代',
    desc: '营销环境瞬息万变，我们保持学习，不断优化方法论与交付标准。',
  },
];

const timeline = [
  { year: '2018', title: '公司成立', desc: '黄瓜传媒在北京成立，专注品牌内容创作' },
  { year: '2019', title: '团队扩张', desc: '团队扩展至20人，服务品牌突破50家' },
  { year: '2020', title: '视频业务线', desc: '建立专业视频制作团队，推出企业宣传片服务' },
  { year: '2021', title: '社媒运营', desc: '新增社交媒体运营业务，覆盖主流内容平台' },
  { year: '2022', title: '数字化转型', desc: '建立数据中台，实现内容效果的全链路追踪' },
  { year: '2023', title: '跨境服务', desc: '推出TikTok跨境营销服务，助力品牌出海' },
  { year: '2024', title: '全案整合', desc: '整合五大业务线，提供一站式品牌传播解决方案' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt')?.toString().trim();

    if (honeypot) {
      setFormState('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        payload.append(key, value.toString());
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qtm6nnahb7382ovfn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormState('success');
        form.reset();
      } else {
        setFormState('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormState('error');
      setFormError('网络异常，请检查网络后重试');
    }
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonical = `${origin}/about`;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 黄瓜传媒',
    description: '了解黄瓜传媒的品牌故事、核心团队、服务理念与联系方式。',
    url: canonical,
    isPartOf: {
      '@type': 'WebSite',
      name: '黄瓜传媒',
      url: origin,
    },
    mainEntity: {
      '@type': 'Organization',
      name: '黄瓜传媒',
      description: '专注于品牌内容创作、数字营销与媒体传播的专业平台。',
      foundingDate: '2018',
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京市',
        addressRegion: '朝阳区',
        streetAddress: '建国路88号SOHO现代城A座2208室',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-6666',
        contactType: '客户服务',
        availableLanguage: 'Chinese',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄瓜传媒成立于哪一年？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜传媒成立于2018年，总部位于北京，是一家专注于品牌内容创作与数字营销的专业机构。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜传媒的核心服务是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜传媒提供品牌策划、视频制作、社交媒体运营、数字营销和创意设计五大核心服务，覆盖品牌传播的全链路需求。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系黄瓜传媒？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过拨打400-888-6666、发送邮件至hello@cucumbermedia.cn，或在官网填写联系表单与我们取得联系。',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOMeta
        title="关于我们 - 黄瓜传媒"
        description="了解黄瓜传媒的品牌故事、核心团队、服务理念与联系方式。黄瓜传媒成立于2018年，专注于品牌内容创作与数字营销。"
        keywords="黄瓜传媒,关于黄瓜传媒,品牌传播公司,内容创作团队,数字营销机构"
        canonical={canonical}
      />
      <SchemaOrg schema={webpageSchema} />
      <SchemaOrg schema={faqSchema} />

      {/* Hero */}
      <section className="relative w-full min-h-[360px] md:min-h-[440px] flex items-center bg-secondary-100">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20organic%20shapes%20in%20warm%20green%20and%20cream%20gradients%2C%20soft%20ethereal%20layered%20composition%2C%20brand%20story%20visualization%20art%2C%20editorial%20art%20style%20with%20smooth%20transitions%2C%20calm%20professional%20atmosphere%2C%20non-photorealistic%20artistic%20abstract%20background&width=1600&height=500&seq=about-hero&orientation=landscape"
            alt="关于黄瓜传媒"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 mb-4">
              关于黄瓜传媒
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              我们相信，好的传播不仅是信息的传递，更是价值的创造。每一次与客户的合作，都是一段共同成长的旅程。
            </p>
          </div>
        </div>
      </section>

      {/* Brand intro */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24" aria-labelledby="brand-intro">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
                品牌故事
              </span>
              <h2 id="brand-intro" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-5">
                用创意与专业，让品牌被看见、被记住、被选择
              </h2>
              <div className="space-y-4 text-foreground-600 leading-relaxed">
                <p>
                  黄瓜传媒成立于2018年，是一家专注于品牌内容创作、数字营销与媒体传播的专业机构。我们汇聚了来自广告、传媒、互联网等领域的专业人才，致力于为企业提供从策略到执行的一站式传播解决方案。
                </p>
                <p>
                  在过去六年中，我们服务了超过200个品牌，涵盖快消、美妆、餐饮、教育、科技、制造等多个行业。我们的服务覆盖品牌策划、视频制作、社交媒体运营、数字营销和创意设计五大领域，累计创造内容曝光超过10亿次。
                </p>
                <p>
                  我们始终坚信，真正有效的传播不是自说自话，而是建立品牌与用户之间的真实连接。每一次项目交付，我们都以结果为导向，用数据验证效果，用创意打动人心。
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Creative%20agency%20team%20collaboration%20scene%20in%20bright%20modern%20open%20office%2C%20diverse%20professionals%20discussing%20around%20table%20with%20laptops%2C%20warm%20natural%20light%2C%20positive%20collaborative%20energy%2C%20editorial%20corporate%20photography%20style%20with%20warm%20neutral%20tones%20and%20subtle%20green%20plant%20accents&width=800&height=600&seq=about-team&orientation=landscape"
                alt="黄瓜传媒团队"
                className="w-full rounded-xl shadow-lg"
              />
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-accent-500 text-white rounded-xl p-4 md:p-5 shadow-lg">
                <p className="text-2xl md:text-3xl font-bold font-heading">6+</p>
                <p className="text-xs md:text-sm text-white/90">年行业深耕</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-background-100" aria-labelledby="values-title">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wide uppercase mb-4">
              核心价值观
            </span>
            <h2 id="values-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              驱动我们前行的信念
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="bg-background-50 rounded-xl p-6 md:p-7 border border-background-200/70 hover:border-primary-300/60 hover:shadow-md transition-all duration-300"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className={`${v.icon} text-xl`} />
                </div>
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-2">
                  {v.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24" aria-labelledby="timeline-title">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
              发展历程
            </span>
            <h2 id="timeline-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              我们的成长轨迹
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-primary-200 md:-translate-x-px" />
            <div className="space-y-8 md:space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70 ml-10 md:ml-0">
                      <span className="text-sm font-semibold text-primary-600">{item.year}</span>
                      <h3 className="text-base font-semibold font-heading text-foreground-900 mt-1 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1.5 top-6 md:top-1/2 md:-translate-y-1.5 z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-secondary-100" aria-labelledby="testimonials-title">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
              客户口碑
            </span>
            <h2 id="testimonials-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              他们这样评价我们
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="bg-background-50 rounded-xl p-6 md:p-7 border border-secondary-200/70"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm" />
                    </span>
                  ))}
                </div>
                <p className="text-foreground-700 leading-relaxed mb-5 text-sm md:text-base">
                  "{t.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-sm text-primary-700 font-semibold">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24" aria-labelledby="contact-title">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wide uppercase mb-4">
                联系我们
              </span>
              <h2 id="contact-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-5">
                让我们开始对话
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-8">
                无论您是想了解我们的服务、探讨合作机会，还是仅仅想聊聊品牌传播的见解，我们都很乐意与您交流。
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-sm text-foreground-600">北京市朝阳区建国路88号SOHO现代城A座2208室</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">联系电话</p>
                    <p className="text-sm text-foreground-600">400-888-6666</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-600">hello@cucumbermedia.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作时间</p>
                    <p className="text-sm text-foreground-600">周一至周五 09:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              {formState === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-check-line text-2xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">提交成功</h3>
                  <p className="text-sm text-foreground-600 mb-4">感谢您的留言，我们会尽快与您取得联系。</p>
                  <button
                    type="button"
                    onClick={() => setFormState('idle')}
                    className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="contact" className="space-y-4">
                  <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-1">在线留言</h3>
                  <p className="text-sm text-foreground-500 mb-4">填写以下信息，我们会在24小时内回复您</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      联系电话
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                      placeholder="请输入您的联系电话"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      公司名称
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                      placeholder="请输入您的公司名称"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      感兴趣的服务
                    </label>
                    <select
                      id="service"
                      name="service"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    >
                      <option value="">请选择服务类型</option>
                      <option value="brand-planning">品牌策划</option>
                      <option value="video-production">视频制作</option>
                      <option value="social-media">社交媒体运营</option>
                      <option value="digital-marketing">数字营销</option>
                      <option value="creative-design">创意设计</option>
                      <option value="full-service">全案整合服务</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                      placeholder="请简要描述您的需求..."
                    />
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>

                  {/* Honeypot */}
                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formState === 'error' && formError && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formState === 'submitting' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}